import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import SectionContainer from '../../components/UI/SectionContainer';
import Spinner from '../../components/UI/Spinner';
import { openAlert } from '../../utils/common';
import {
  CALIBRATION_PHASE_LABEL,
  CALIBRATION_STATUS_LABEL,
  canViewCalibrationMemberInfo,
  caseIdToUserIdx,
} from '../../utils/calibrationAdmin';
import calibrationService from '../../services/calibrationService';
import aiModelService from '../../services/aiModelService';
import { useAuth } from '../../context/AuthContext';

const MAX_SIZE_MB = 50;
const ALLOWED_EXT = ['.dat', '.bin', '.tflite', '.onnx'];
const META_EXT = ['.meta', '.bin', '.dat'];
const META_SIZE_BYTES = 48;

/** 업로드 결과 안내 — 같은 내용이면 앱에 업데이트가 제공되지 않는다 */
function describeUploadResult(data) {
  const d = data || {};
  const hash = String(d.sha256 || '').slice(0, 12);
  if (d.same_as_previous) {
    return `업로드는 됐지만 직전 모델과 내용이 같습니다. 앱에는 업데이트가 제공되지 않습니다. (해시 ${hash})`;
  }
  return `AI 모델 패키지 업로드가 완료되었습니다. 라벨 ${d.version || '-'} · 해시 ${hash}`;
}

const CalibrationCasePage = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const { role } = useAuth();
  const canViewMember = canViewCalibrationMemberInfo(role);
  const userIdxFromCase = caseIdToUserIdx(caseId);

  const [loading, setLoading] = useState(false);
  const [caseData, setCaseData] = useState(null);
  const [items, setItems] = useState([]);
  const [file, setFile] = useState(null);
  const [metaFile, setMetaFile] = useState(null);
  const fileInputRef = useRef();
  const metaInputRef = useRef();

  const resolveUserIdx = useCallback(
    (data) => {
      if (canViewMember && data?.user_idx) return Number(data.user_idx);
      return userIdxFromCase;
    },
    [canViewMember, userIdxFromCase],
  );

  const fetchCase = useCallback(async () => {
    if (!caseId) return;
    setLoading(true);
    try {
      const res = await calibrationService.getCase(caseId);
      if (!res.success) {
        openAlert({ title: '알림', description: res.message || '케이스 조회에 실패했습니다.' });
        return;
      }
      setCaseData(res.data);
      const uid = resolveUserIdx(res.data);
      if (uid) {
        const listRes = await aiModelService.getList(uid);
        if (listRes.success) {
          setItems(listRes.data?.items || []);
        }
      }
    } catch (e) {
      const msg = e.response?.data?.message || e.message || '케이스 조회 중 오류가 발생했습니다.';
      openAlert({ title: '알림', description: msg });
    } finally {
      setLoading(false);
    }
  }, [caseId, resolveUserIdx]);

  useEffect(() => {
    if (!userIdxFromCase) {
      openAlert({ title: '알림', description: '올바른 케이스 ID가 아닙니다.' });
      navigate('/calibration/queue', { replace: true });
      return;
    }
    fetchCase();
  }, [userIdxFromCase, fetchCase, navigate]);

  const handleDownloadRaw = async () => {
    setLoading(true);
    try {
      const res = await calibrationService.exportRawChunks({ caseId });
      if (!res.success) {
        openAlert({ title: '알림', description: res.message || 'raw 데이터 조회에 실패했습니다.' });
        return;
      }
      const rowCount = res.data?.row_count ?? res.data?.count ?? 0;
      const csv = res.data?.csv;
      if (!csv || rowCount === 0) {
        openAlert({ title: '알림', description: '다운로드할 보정 raw CSV 행이 없습니다.' });
        return;
      }
      const filename = res.data?.filename || `calibration_raw_${caseId}.csv`;
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      const skipped = res.data?.skipped?.length ?? 0;
      openAlert({
        title: '알림',
        description:
          skipped > 0
            ? `학습용 CSV ${rowCount}행 다운로드 완료 (스킵 ${skipped}건)`
            : `학습용 CSV ${rowCount}행 다운로드 완료`,
      });
    } catch (e) {
      const msg = e.response?.data?.message || e.message || 'raw 데이터 다운로드 중 오류가 발생했습니다.';
      openAlert({ title: '알림', description: msg });
    } finally {
      setLoading(false);
    }
  };

  const validateFile = (f) => {
    if (f.size > MAX_SIZE_MB * 1024 * 1024) {
      return `파일 크기는 ${MAX_SIZE_MB}MB 이하여야 합니다.`;
    }
    const name = f.name.toLowerCase();
    if (!ALLOWED_EXT.some((ext) => name.endsWith(ext))) {
      return `허용된 모델 파일 형식: ${ALLOWED_EXT.join(', ')}`;
    }
    return null;
  };

  const validateMetaFile = (f) => {
    if (f.size !== META_SIZE_BYTES) {
      return `AI Meta 파일은 정확히 ${META_SIZE_BYTES}바이트여야 합니다. (현재 ${f.size}B)`;
    }
    const name = f.name.toLowerCase();
    if (!META_EXT.some((ext) => name.endsWith(ext))) {
      return `허용된 meta 파일 형식: ${META_EXT.join(', ')}`;
    }
    return null;
  };

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const err = validateFile(f);
    if (err) {
      openAlert({ title: '알림', description: err });
      e.target.value = '';
      return;
    }
    setFile(f);
  };

  const handleMetaChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const err = validateMetaFile(f);
    if (err) {
      openAlert({ title: '알림', description: err });
      e.target.value = '';
      return;
    }
    setMetaFile(f);
  };

  const handleUpload = async () => {
    const uid = resolveUserIdx(caseData);
    if (!file || !metaFile || !uid) {
      openAlert({ title: '알림', description: '모델 파일과 meta 파일(48B)을 모두 선택해주세요.' });
      return;
    }
    if (!window.confirm(`케이스 ${caseId}에 AI 모델 패키지(모델+meta)를 업로드하시겠습니까?`)) return;
    setLoading(true);
    try {
      const res = await aiModelService.upload(file, metaFile, uid);
      if (res.success) {
        openAlert({ title: '알림', description: describeUploadResult(res.data) });
        setFile(null);
        setMetaFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        if (metaInputRef.current) metaInputRef.current.value = '';
        fetchCase();
      } else {
        openAlert({ title: '알림', description: res.message || 'AI 모델 업로드에 실패했습니다.' });
      }
    } catch (e) {
      const msg = e.response?.data?.message || e.message || 'AI 모델 업로드 중 오류가 발생했습니다.';
      openAlert({ title: '알림', description: msg });
    } finally {
      setLoading(false);
    }
  };

  if (!caseData) {
    return (
      <SectionContainer title="보정 처리">
        <Spinner visible={loading} />
      </SectionContainer>
    );
  }

  const memberUserIdx = canViewMember ? (caseData.user_idx || userIdxFromCase) : null;

  return (
    <SectionContainer title={`보정 처리 — ${caseId}`}>
      <Spinner visible={loading} />
      <p style={{ marginBottom: 12 }}>
        <Link to="/calibration/queue">← 보정 대기열</Link>
      </p>

      <div style={{ display: 'grid', gap: 12, marginBottom: 20 }}>
        <div><strong>케이스 ID:</strong> {caseId}</div>
        <div><strong>상태:</strong> {CALIBRATION_STATUS_LABEL[caseData.status] || caseData.status}</div>
        <div><strong>진행 단계:</strong> {CALIBRATION_PHASE_LABEL[caseData.phase] || caseData.phase}</div>
        <div><strong>진행률:</strong> {caseData.total_count}/{caseData.total_max}</div>
        <div><strong>raw 측정:</strong> {caseData.raw_pair_count ?? 0}건 · <strong>등록 모델:</strong> {caseData.user_model_count}개</div>
        {canViewMember && caseData.user_name && (
          <div>
            <strong>회원:</strong>{' '}
            {memberUserIdx ? (
              <Link to={`/user/profile/${memberUserIdx}`}>
                {caseData.user_name}
                {caseData.user_id ? ` (${caseData.user_id})` : ''}
              </Link>
            ) : (
              <>
                {caseData.user_name}
                {caseData.user_id ? ` (${caseData.user_id})` : ''}
              </>
            )}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 280, border: '1px solid #e5e7eb', borderRadius: 10, padding: 16 }}>
          <h3 style={{ fontSize: 16, marginBottom: 8 }}>① 보정 raw 데이터 다운로드</h3>
          <p style={{ color: '#666', fontSize: 14, marginBottom: 12 }}>
            AI 학습용 CSV를 다운로드합니다. (2048 raw + 아큐첵 label + 개체코드 + 측정일시)
          </p>
          <button type="button" className="btn btn-primary" onClick={handleDownloadRaw} disabled={!caseData.has_raw_data}>
            raw CSV 다운로드
          </button>
        </div>

        <div style={{ flex: 1, minWidth: 280, border: '1px solid #e5e7eb', borderRadius: 10, padding: 16 }}>
          <h3 style={{ fontSize: 16, marginBottom: 8 }}>② AI 모델 패키지 업로드</h3>
          <p style={{ color: '#666', fontSize: 13, marginBottom: 10 }}>
            모델({ALLOWED_EXT.join(', ')}) + meta({META_SIZE_BYTES}B, {META_EXT.join(', ')})
          </p>
          <input ref={fileInputRef} type="file" accept={ALLOWED_EXT.join(',')} onChange={handleFileChange} style={{ display: 'none' }} />
          <input ref={metaInputRef} type="file" accept={META_EXT.join(',')} onChange={handleMetaChange} style={{ display: 'none' }} />
          <button type="button" className="btn btn-secondary" onClick={() => fileInputRef.current?.click()} style={{ marginBottom: 8, marginRight: 8 }}>
            모델 선택
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => metaInputRef.current?.click()} style={{ marginBottom: 8 }}>
            Meta 선택
          </button>
          <div style={{ fontSize: 13, marginBottom: 4, color: file ? '#222' : '#999' }}>
            모델: {file ? file.name : '없음'}
          </div>
          <div style={{ fontSize: 13, marginBottom: 8, color: metaFile ? '#222' : '#999' }}>
            Meta: {metaFile ? `${metaFile.name} (${metaFile.size}B)` : '없음'}
          </div>
          <button type="button" className="btn btn-primary" onClick={handleUpload} disabled={!file || !metaFile}>
            패키지 업로드
          </button>
        </div>
      </div>

      <h3 style={{ fontSize: 16, marginTop: 24, marginBottom: 8 }}>등록된 AI 모델</h3>
      <table className="table" style={{ width: '100%', fontSize: 13 }}>
        <thead>
          <tr>
            <th>라벨</th>
            <th>해시</th>
            <th>모델</th>
            <th>Meta</th>
            <th>대상</th>
            <th>등록일</th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 ? (
            <tr><td colSpan={6} style={{ textAlign: 'center', color: '#888' }}>등록된 모델이 없습니다.</td></tr>
          ) : (
            items.map((m) => (
              <tr key={m.idx}>
                <td>{m.version || '-'}</td>
                <td title={m.sha256 || ''}>{m.sha256 ? String(m.sha256).slice(0, 12) : '-'}</td>
                <td>{m.bin || '-'}</td>
                <td>{m.meta_bin || '-'}</td>
                <td>{m.user_idx ? `케이스 ${caseId}` : '글로벌'}</td>
                <td>{m.regdate || '-'}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </SectionContainer>
  );
};

export default CalibrationCasePage;
